import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Users } from "lucide-react";
import StoreBadges from "@/components/brand/StoreBadges";
import SocialRow from "@/components/brand/SocialRow";
import TelegramIcon from "@/components/brand/TelegramIcon";
import { SOCIALS, STATS, WHATSAPP } from "@/lib/brand";

const COMPANY_LINKS = [
    { name: "من نحن", path: "/about" },
    { name: "مشاريعنا", path: "/portfolio" },
    { name: "خدماتنا", path: "/#services" },
    { name: "تواصل معنا", path: "/contact" },
];

const KNOWLEDGE_LINKS = [
    { name: "المدونة", path: "/blog" },
    { name: "الأسئلة الشائعة", path: "/faq" },
    { name: "سياسة الخصوصية", path: "/privacy" },
    { name: "الشروط والأحكام", path: "/terms" },
];

const PublicFooter = () => (
    <footer className="bg-background pt-32 pb-12 border-t border-white/5 relative overflow-hidden" dir="rtl">
        <div className="absolute inset-x-0 top-0 h-40 bg-grid opacity-[0.03] pointer-events-none" />

        <div className="container mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-16 mb-24">
                {/* Brand */}
                <div className="lg:col-span-12 xl:col-span-5">
                    <Link to="/" className="flex items-center gap-3 group mb-8">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center group-hover:bg-primary transition-colors">
                            <span className="text-background font-black text-xl">T</span>
                        </div>
                        <span className="text-3xl font-black tracking-tighter text-white">TRBD</span>
                    </Link>
                    <p className="text-xl text-muted-foreground font-medium mb-10 max-w-md leading-relaxed">
                        نحن لا نبني برمجيات فقط، بل نؤطر مستقبل أعمالك الرقمي. حلول سورية بمعايير عالمية.
                    </p>

                    <SocialRow
                        variant="icon"
                        channels={['facebook', 'linkedin', 'youtube', 'telegram']}
                        buttonSize={48}
                        iconSize={18}
                        className="mb-8"
                    />

                    <div>
                        <p className="text-xs font-black text-white/40 uppercase tracking-widest mb-3">حمّل تطبيقاتنا</p>
                        <StoreBadges />
                    </div>
                </div>

                {/* Company nav */}
                <FooterLinkColumn heading="الشركة" links={COMPANY_LINKS} className="lg:col-span-4 xl:col-span-2" />

                {/* Knowledge nav */}
                <FooterLinkColumn heading="المعرفة" links={KNOWLEDGE_LINKS} className="lg:col-span-4 xl:col-span-2" />

                {/* Contact */}
                <div className="lg:col-span-4 xl:col-span-3">
                    <h3 className="text-sm font-black text-white/40 uppercase tracking-widest mb-8">تواصل</h3>
                    <div className="space-y-6">
                        <div className="flex items-start gap-4">
                            <MapPin size={20} className="text-primary mt-1 shrink-0" />
                            <span className="text-lg font-bold text-muted-foreground leading-snug">
                                دمشق، المزة، أوتوستراد المزة الرئيسي
                            </span>
                        </div>
                        <div className="flex items-center gap-4">
                            <Mail size={20} className="text-primary shrink-0" />
                            <a href="mailto:info@trbd.net" className="text-lg font-bold text-muted-foreground hover:text-white transition-colors">
                                info@trbd.net
                            </a>
                        </div>
                        <ContactChannel
                            icon={<Phone size={20} />}
                            primary={WHATSAPP.tr.display}
                            caption="WhatsApp"
                            href={WHATSAPP.tr.url}
                        />
                        <ContactChannel
                            icon={<Phone size={20} />}
                            primary={WHATSAPP.sy.display}
                            caption="WhatsApp"
                            href={WHATSAPP.sy.url}
                        />
                        <ContactChannel
                            icon={<TelegramIcon size={20} />}
                            primary="@trbd_sybot"
                            caption="Telegram Bot"
                            href={SOCIALS.telegram.url}
                        />
                    </div>
                </div>
            </div>

            <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
                <p className="text-sm font-bold text-white/20 tracking-wide uppercase">
                    © {new Date().getFullYear()} TRBD DIGITAL SOLUTIONS. صنع بحب من دمشق.
                </p>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                    <BottomPill icon={<Users className="w-3 h-3" />}>
                        {STATS.clients} client worldwide
                    </BottomPill>
                    <BottomPill icon={<span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />}>
                        جميع الأنظمة تعمل بكفاءة
                    </BottomPill>
                </div>
            </div>
        </div>
    </footer>
);

const FooterLinkColumn = ({
    heading,
    links,
    className,
}: {
    heading: string;
    links: { name: string; path: string }[];
    className?: string;
}) => (
    <div className={className}>
        <h3 className="text-sm font-black text-white/40 uppercase tracking-widest mb-8">{heading}</h3>
        <ul className="space-y-4">
            {links.map((link) => (
                <li key={link.path}>
                    <Link to={link.path} className="text-lg font-bold text-muted-foreground hover:text-white transition-colors">
                        {link.name}
                    </Link>
                </li>
            ))}
        </ul>
    </div>
);

/**
 * WhatsApp / Telegram row in the contact column.
 * Two-line layout (value + caption) with a consistent leading glyph.
 */
const ContactChannel = ({
    icon,
    primary,
    caption,
    href,
}: {
    icon: React.ReactNode;
    primary: string;
    caption: string;
    href: string;
}) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
        <span className="text-primary shrink-0">{icon}</span>
        <span className="flex flex-col">
            <span className="text-lg font-bold text-muted-foreground group-hover:text-white transition-colors font-mono" dir="ltr">
                {primary}
            </span>
            <span className="text-xs text-white/40 font-bold uppercase tracking-widest">{caption}</span>
        </span>
    </a>
);

const BottomPill = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
    <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-black uppercase tracking-widest text-white/40">
        {icon}
        {children}
    </div>
);

export default PublicFooter;
